import {
  Card,
  CardBody,
  CardFooter,
  CardTitle,
  Col,
  Modal,
  Row,
  Table,
} from "reactstrap"
import { MdDelete, MdModeEdit } from "react-icons/md"
import { FaEye, FaSortAlphaDown, FaSortAlphaUp } from "react-icons/fa"
import { useContext, useEffect, useRef, useState } from "react"
import { DataFilter, ItemsPerPage, SearchFilter, SelectFilter } from "components/Common/filters"
import { useSelector, useDispatch } from "react-redux"
import {
  allPayments,
  PaymentDelete,
  PaymentFetch,
} from "redux/slice/payment/paymentSlice"
import PaginationComponent from "components/Common/pagination"
import Loader from "components/Common/loader"
import SidebarContext from "context/sidebar.context"
import { SHUK_ROLE_VIWEWER } from "redux/enums"
import { userData } from "redux/slice/user/userSlice"
import { PAYMENT_MODE_OPTIONS } from "common/data/paymentOptions"

const INITIAL_FILTERS = {
  applicationId: "",
  partnerId: "",
  studentName: "",
  currentYear: "",
  currentSemester: "",
  isPaid: "",
  amountComitted: "",
  amountPaid: "",
  paymentDueDate: "",
  paymentMadeDate: "",
  challanNumber: "",
  paymentMode: "",
  notes: "",
  country: "",
}

const IS_PAID_FILTER_OPTIONS = [
  { id: "true", name: "Paid" },
  { id: "false", name: "Unpaid" },
]

const SORTABLE_COLUMNS = [
  { label: "Application ID", sortKey: "ApplicationId" },
  { label: "Partner ID", sortKey: "PartnerId" },
  { label: "Student Name", sortKey: "StudentName" },
  { label: "Year", sortKey: "CurrentYear" },
  { label: "Semester", sortKey: "CurrentSemester" },
  { label: "Payment Status", sortKey: "IsPaid" },
  { label: "Amount Committed", sortKey: "AmountCommitted" },
  { label: "Amount Paid", sortKey: "AmountPaid" },
  { label: "Challan Number", sortKey: "ChallanNumber" },
  { label: "Modes of Payment", sortKey: "PaymentMode" },
  { label: "Country", sortKey: "Country" },
]

export default function PaymentTable(props) {
  const dispatch = useDispatch()
  const { userRole } = useSelector(userData)
  const { paymentData, loading, totalPages } = useSelector(allPayments)
  const {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    paymentFilters: filters,
    setPaymentFilters: setFilters,
  } = useContext(SidebarContext)
  const [deleteIdState, setDeleteIdState] = useState(null)
  const [deleteModalState, setDeleteModalState] = useState(false)
  const [sortBy, setSortBy] = useState({ label: "", order: false })
  const debounceRef = useRef(null)
  const firstFilterRender = useRef(true)
  const firstSortRender = useRef(true)

  const isViewer = userRole === SHUK_ROLE_VIWEWER

  const buildParams = (pageNumber, f) => {
    const params = { pageNumber, pageSize: itemsPerPage }
    if (f.applicationId !== "") params.ApplicationId = f.applicationId
    if (f.partnerId !== "") params.PartnerId = f.partnerId
    if (f.studentName !== "") params.StudentName = f.studentName
    if (f.currentYear !== "") params.CurrentYear = f.currentYear
    if (f.currentSemester !== "") params.CurrentSemester = f.currentSemester
    if (f.isPaid !== "") params.isPaid = f.isPaid
    if (f.amountComitted !== "") params.AmountComitted = f.amountComitted
    if (f.amountPaid !== "") params.AmountPaid = f.amountPaid
    if (f.paymentDueDate !== "")
      params.PaymentDueDate = new Date(f.paymentDueDate).toISOString()
    if (f.paymentMadeDate !== "")
      params.PaymentMadeDate = new Date(f.paymentMadeDate).toISOString()
    if (f.challanNumber !== "") params.ChallanNumber = f.challanNumber
    if (f.paymentMode !== "") params.PaymentMode = f.paymentMode
    if (f.notes !== "") params.Notes = f.notes
    if (f.country !== "") params.Country = f.country
    // Sorting via clickable column headers (same pattern as Applications).
    params.asce = sortBy.order
    if (sortBy.label) params.SortBy = sortBy.label
    return params
  }

  const handleSorting = _sortby => {
    if (sortBy.label === _sortby) {
      setSortBy(prev => ({ ...prev, order: !sortBy.order }))
    } else {
      setSortBy({ label: _sortby, order: true })
    }
  }

  const fetchPayments = (pageNumber, f) => {
    dispatch(PaymentFetch(buildParams(pageNumber, f)))
  }

  const paginate = pageNumber => {
    setCurrentPage(pageNumber)
    fetchPayments(pageNumber, filters)
  }

  useEffect(() => {
    fetchPayments(currentPage, filters)
  }, [itemsPerPage])

  useEffect(() => {
    if (firstFilterRender.current) {
      firstFilterRender.current = false
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setCurrentPage(1)
      fetchPayments(1, filters)
    }, 900)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [filters])

  useEffect(() => {
    if (firstSortRender.current) {
      firstSortRender.current = false
      return
    }
    setCurrentPage(1)
    fetchPayments(1, filters)
  }, [sortBy])

  const handleItemsPerPage = perPage => {
    if (perPage !== itemsPerPage) {
      setCurrentPage(1)
    }
    setItemsPerPage(Number(perPage))
  }

  const handleChangeFilter = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }))
  }

  const handleReset = () => {
    setFilters(INITIAL_FILTERS)
  }

  const handleUpdate = received => {
    props.forUpdate(received)
  }

  const handleView = received => {
    props.forView(received)
  }

  const handleDelete = async () => {
    dispatch(PaymentDelete(deleteIdState?.id))
    setDeleteModalState(false)
  }

  const formatDate = value => {
    if (!value) return "-"
    return value.slice(0, 10)
  }

  return (
    <div className="payment-table-container">
      <Row>
        <Col lg={12}>
          <Card className="app-table-card">
            <CardTitle className="fs-2 dot-after ms-3">
              Payment Management
            </CardTitle>
            <CardBody>
              <Row className="">
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  <SearchFilter
                    label="Application ID"
                    type="number"
                    value={filters.applicationId}
                    onChange={e =>
                      handleChangeFilter("applicationId", e.target.value)
                    }
                  />
                </Col>
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  <SearchFilter
                    label="Partner ID"
                    value={filters.partnerId}
                    onChange={e =>
                      handleChangeFilter("partnerId", e.target.value)
                    }
                  />
                </Col>
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  <SearchFilter
                    label="Student Name"
                    value={filters.studentName}
                    onChange={e =>
                      handleChangeFilter("studentName", e.target.value)
                    }
                  />
                </Col>
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  <DataFilter
                    label="Country"
                    options={[
                      // {id: "", name: "All"},
                      { id: "PK", name: "Pakistan" },
                      { id: "IN", name: "India" },
                      { id: "BD", name: "Bangladesh" },
                      { id: "LK", name: "Sri Lanka" },
                    ]}
                    value={filters.country}
                    onChange={e =>
                      handleChangeFilter("country", e.target.value)
                    }
                  />
                  {/* <SearchFilter
                    label="Country"
                    value={filters.country}
                    onChange={e =>
                      handleChangeFilter("country", e.target.value)
                    }
                  /> */}
                </Col>
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  <SearchFilter
                    label="Current Year"
                    value={filters.currentYear}
                    onChange={e =>
                      handleChangeFilter("currentYear", e.target.value)
                    }
                  />
                </Col>
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  <SearchFilter
                    label="Current Semester"
                    value={filters.currentSemester}
                    onChange={e =>
                      handleChangeFilter("currentSemester", e.target.value)
                    }
                  />
                </Col>
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  <SelectFilter
                    label="Is Paid"
                    placeholder="All"
                    options={IS_PAID_FILTER_OPTIONS}
                    value={filters.isPaid}
                    onChange={e => handleChangeFilter("isPaid", e.target.value)}
                  />
                </Col>
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  <SearchFilter
                    label="Amount Committed"
                    type="number"
                    value={filters.amountComitted}
                    onChange={e =>
                      handleChangeFilter("amountComitted", e.target.value)
                    }
                  />
                </Col>
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  <SearchFilter
                    label="Amount Paid"
                    type="number"
                    value={filters.amountPaid}
                    onChange={e =>
                      handleChangeFilter("amountPaid", e.target.value)
                    }
                  />
                </Col>
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  <SearchFilter
                    label="Payment Due Date"
                    type="date"
                    value={filters.paymentDueDate}
                    onChange={e =>
                      handleChangeFilter("paymentDueDate", e.target.value)
                    }
                  />
                </Col>
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  <SearchFilter
                    label="Payment Made Date"
                    type="date"
                    value={filters.paymentMadeDate}
                    onChange={e =>
                      handleChangeFilter("paymentMadeDate", e.target.value)
                    }
                  />
                </Col>
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  <SearchFilter
                    label="Challan Number"
                    value={filters.challanNumber}
                    onChange={e =>
                      handleChangeFilter("challanNumber", e.target.value)
                    }
                  />
                </Col>
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  {/* <SearchFilter
                    label="Payment Mode"
                    value={filters.paymentMode}
                    onChange={e =>
                      handleChangeFilter("paymentMode", e.target.value)
                    }
                  /> */}
                  <DataFilter
                    label="Payment Mode"
                    options={PAYMENT_MODE_OPTIONS}
                    value={filters.paymentMode}
                    onChange={e =>
                      handleChangeFilter("paymentMode", e.target.value)
                    }
                  />
                </Col>
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  <SearchFilter
                    label="Notes"
                    value={filters.notes}
                    onChange={e => handleChangeFilter("notes", e.target.value)}
                  />
                </Col>
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  <ItemsPerPage
                    value={itemsPerPage}
                    handleItemsPerPage={handleItemsPerPage}
                  />
                </Col>
                <Col xs={6} sm={4} md={4} lg={2} xl={2}>
                  <button
                    className="btn btn-outline-danger w-100"
                    style={{
                      height: "calc(3.5rem + calc(var(--bs-border-width) * 2))",
                    }}
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                </Col>
              </Row>

              <div className="app-table-body">
                {loading ? (
                  <Loader />
                ) : (
                  <Table className="table mb-0 table-bordered">
                    <thead>
                      <tr>
                        {SORTABLE_COLUMNS.map(col => (
                          <th
                            key={col.sortKey}
                            className="table-header-sort text-nowrap"
                            onClick={() => handleSorting(col.sortKey)}
                          >
                            <div className="header-content">
                              <span className="text-nowrap table-head-label">
                                {col.label}
                              </span>
                              {sortBy.label === col.sortKey &&
                                sortBy.order && <FaSortAlphaDown color="red" />}
                              {sortBy.label === col.sortKey &&
                                !sortBy.order && <FaSortAlphaUp color="red" />}
                            </div>
                          </th>
                        ))}
                        <th className="text-nowrap">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentData && paymentData.length > 0 ? (
                        paymentData.map(item => (
                          <tr
                            key={item?.id}
                            style={{ cursor: "pointer" }}
                            onClick={() => handleView(item)}
                          >
                            <td className="text-nowrap">
                              {item?.applicationId ?? "-"}
                            </td>
                            <td className="text-nowrap">
                              {item?.partnerId || "-"}
                            </td>
                            <td className="text-nowrap">
                              {item?.studentName || "-"}
                            </td>
                            <td className="text-nowrap">
                              {item?.currentYear || "-"}
                            </td>
                            <td className="text-nowrap">
                              {item?.currentSemester || "-"}
                            </td>
                            <td className="text-nowrap">
                              {item?.isPaid ? (
                                <span className="badge bg-success">Paid</span>
                              ) : (
                                <span className="badge bg-warning">Unpaid</span>
                              )}
                            </td>
                            <td className="text-nowrap">
                              {item?.amountCommitted || "-"}
                            </td>
                            <td className="text-nowrap">
                              {item?.amountPaid || "-"}
                            </td>
                            <td className="text-nowrap">
                              {item?.challanNumber || "-"}
                            </td>
                            <td className="text-nowrap">
                              {item?.paymentModeEnum || "-"}
                            </td>
                            <td className="text-nowrap">
                              {item?.country || "-"}
                            </td>
                            <td
                              className="d-flex"
                              onClick={e => e.stopPropagation()}
                            >
                              <FaEye
                                style={{ cursor: "pointer" }}
                                className="me-2 text-secondary"
                                onClick={() => handleView(item)}
                                size={18}
                              />
                              {!isViewer && (
                                <MdModeEdit
                                  style={{ cursor: "pointer" }}
                                  className="me-2 text-primary"
                                  onClick={() => handleUpdate(item)}
                                  size={18}
                                />
                              )}
                              {/* Delete is hidden until the delete endpoint is
                                  confirmed. The delete flow (thunk + modal +
                                  handleDelete) is kept ready — just uncomment
                                  this and confirm the endpoint in
                                  paymentApiHelper.js. */}
                              {/* {!isViewer && (
                                <MdDelete
                                  style={{ cursor: "pointer" }}
                                  className="text-danger"
                                  onClick={() => {
                                    setDeleteIdState(item)
                                    setDeleteModalState(true)
                                  }}
                                  size={18}
                                />
                              )} */}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="12" className="text-center">
                            No data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </div>
            </CardBody>
            <CardFooter>
              <div className="d-flex justify-content-center justify-content-md-end justify-content-lg-end align-items-center flex-wrap">
                <div style={{ marginLeft: "4px" }}>
                  <PaginationComponent
                    totalPages={totalPages}
                    currentPage={currentPage}
                    paginate={paginate}
                  />
                </div>
              </div>
            </CardFooter>
          </Card>
        </Col>
      </Row>
      <Modal isOpen={deleteModalState} centered>
        <Card className="bg-white">
          <CardBody className="mt-2">
            <h5 style={{ fontWeight: "normal" }}>
              Are You Sure. You want to Delete ?
            </h5>
          </CardBody>
          <CardFooter>
            <div className="mt-4 float-end">
              <button
                className="btn btn-danger me-2"
                onClick={() => handleDelete()}
              >
                Yes
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setDeleteModalState(false)}
              >
                No
              </button>
            </div>
          </CardFooter>
        </Card>
      </Modal>
    </div>
  )
}
